import {
  Alert,
  ButtonBase,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useSetRegionMutation } from "Api/Mutations/Auth/useSetRegionMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { CzechiaFlag } from "Components/Shared/LanguageSwitcher/Flags/Czechia";
import { SlovakiaFlag } from "Components/Shared/LanguageSwitcher/Flags/Slovakia";
import { useRegionList } from "Hooks/Options/useRegionList";
import { Region, type SignInResult } from "Infrastructure/Api/Api";
import { getUserInfoAsync } from "State/Auth/UserInfo/GetUserInfoState";
import { Resources, useResource } from "Translations/Resources";
import { setToken } from "Utils/Storage/LocalStorage/LocalStorageUtils";
import { type ReactNode, useState } from "react";
import { useDispatch } from "react-redux";

const RESOURCES = Resources.Components.RegionRequiredModal;

const regionFlags: Record<Region, ReactNode> = {
  [Region.CZ]: <CzechiaFlag size={56} />,
  [Region.SK]: <SlovakiaFlag size={56} />,
};

/**
 * Region-selection step of the ProfileCompletionModal (formerly RegionRequiredModal's body).
 * Setting the region returns a fresh token carrying the region claim; persisting it and
 * refreshing user-info clears regionRequired, which advances the requirement chain.
 */
export const RegionRequirementStep = () => {
  const { t } = useResource();
  const dispatch = useDispatch();
  const { codeListRegions, isLoading, isError } = useRegionList();
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [hasError, setHasError] = useState(false);

  const { mutate: setRegion, isPending } = useSetRegionMutation({
    onSuccess: (result: SignInResult) => {
      setToken(result);
      dispatch(getUserInfoAsync.request(undefined));
    },
    onError: () => {
      setHasError(true);
    },
  });

  const handleConfirm = () => {
    if (!selectedRegion) {
      return;
    }

    setHasError(false);
    setRegion(selectedRegion);
  };

  return (
    <Stack gap={4}>
      <Stack gap={1.5}>
        <Typography variant="h6">{t(RESOURCES.Title)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t(RESOURCES.Description)}
        </Typography>
      </Stack>

      {isLoading && (
        <Stack alignItems="center" py={2}>
          <CircularProgress />
        </Stack>
      )}

      {isError && <Alert severity="error">{t(RESOURCES.LoadingError)}</Alert>}

      {!isLoading && !isError && (
        <Stack direction="row" gap={2}>
          {codeListRegions.map(({ code, name, disabled }) => {
            const isSelected = selectedRegion === code;

            return (
              <ButtonBase
                key={code}
                onClick={() => setSelectedRegion(code)}
                disabled={isPending || disabled}
                sx={{ flex: 1, borderRadius: "10px" }}
              >
                <Paper
                  variant="outlined"
                  sx={({ palette }) => ({
                    width: "100%",
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                    borderRadius: "10px",
                    borderWidth: 2,
                    borderColor: isSelected
                      ? palette.primary.main
                      : palette.divider,
                    backgroundColor: isSelected
                      ? palette.action.selected
                      : palette.background.paper,
                    // A market that is not open yet stays visible ("coming soon" is part of its
                    // name from useRegionList) but reads as inactive.
                    opacity: disabled ? 0.5 : 1,
                    transition: "border-color 0.15s, background-color 0.15s",
                  })}
                >
                  {regionFlags[code]}
                  <Typography variant="subtitle1" fontWeight={600}>
                    {name}
                  </Typography>
                </Paper>
              </ButtonBase>
            );
          })}
        </Stack>
      )}

      {hasError && <Alert severity="error">{t(RESOURCES.Error)}</Alert>}

      <PrimaryButton
        fullWidth
        color="primary"
        onClick={handleConfirm}
        disabled={!selectedRegion}
        isLoading={isPending}
      >
        {t(RESOURCES.Confirm)}
      </PrimaryButton>
    </Stack>
  );
};
